import 'reflect-metadata';

import {Exclude, Expose} from 'class-transformer';
import {IsDefined, IsEnum, ValidateNested, ValidationError} from 'class-validator';

import {computed} from '@angular/core';
import {Prettify} from '@ngrx/signals';
import {uuidv4} from '@shared-types';

import {BaseCycleStatusTriggerEntity, BaseTriggerEntity, ClientEventTriggerEntity, NotificationTimeoutTriggerEntity, StationStatusTriggerEntity} from '../';
import {TriggerActionEntity} from './action';
import {TriggerEventType} from './event';
import {TriggerReset} from './trigger.types';

export abstract class AbstractTriggerEntity {
	@Exclude() public readonly validity: ClassValidatorSignals = classValidatorSignals.bind(this)();

	/** unique identifier for the trigger class/type	*/
	static _uniqueId = '';
	/** unique identifier for the trigger class/type	*/
	@Exclude() public get uniqueId(): string {
		return this.constructor['_uniqueId'];
	}

	/** identifier for the trigger instance	*/
	@Exclude() public id: string = uuidv4();

	@Expose()
	@IsEnum(TriggerEventType, {message: 'trigger.errors.invalidEventType'})
	@IsDefined({message: 'trigger.errors.invalidEventType'})
	public readonly event: TriggerEventType;

	@Expose() @ValidateNested() @IsDefined({message: 'trigger.errors.invalidTriggerAction'}) public abstract action: TriggerActionEntity;

	@Expose() public triggered = false;

	@Expose() public completed = false;

	@Expose() public reset: TriggerReset = TriggerReset.Never;

	@Exclude() public readonly isEventStepValid$ = computed(() => {
		const errors: ValidationError[] = this.validity.errors$();
		return errors.length === 0 || errors.every(e => e.property === 'action');
	});

	protected constructor(event: TriggerEventType, init?: Partial<AbstractTriggerEntity>) {
		Object.assign(this, init);
		this.event = event;
	}

	abstract toJSON(): Prettify<TriggerEntity>;
}

/* -------------------------------------------------------------------------------------------------------------- */

export type TriggerEntity<T extends AbstractTriggerEntity = AbstractTriggerEntity> = T &
	(BaseTriggerEntity | BaseCycleStatusTriggerEntity | StationStatusTriggerEntity | ClientEventTriggerEntity | NotificationTimeoutTriggerEntity);

export function isTriggerEntity(value: any): value is TriggerEntity {
	return value instanceof BaseCycleStatusTriggerEntity || value instanceof StationStatusTriggerEntity || value instanceof ClientEventTriggerEntity || value instanceof NotificationTimeoutTriggerEntity;
}
